/**
 * Export helpers for SQL-editor results held as an Arrow JS `Table`.
 *
 * CSV / TSV / JSON / Excel are built client-side from the table (optionally
 * only the rows currently shown — filtered + sorted — in that order). Parquet
 * and Arrow re-use the IPC bytes exactly as the API sent them, so column types
 * survive untouched (decimal128 stays decimal128, timestamp[us] stays
 * timestamp[us]); parquet-wasm is loaded on first use.
 */
import type { Table as ArrowTable } from 'apache-arrow';
import { copyWithCopyCommand } from './gridSelection';
import { arrowValueToJson, formatArrowValue } from './arrowFormat';

/** Quote a field for a delimited file (RFC 4180 style; NULL → empty). */
const quoteField = (text: string | null, delimiter: string): string => {
  if (text === null) return '';
  const needsQuotes = text.includes(delimiter) || text.includes('"') || text.includes('\n') || text.includes('\r');
  return needsQuotes ? `"${text.replace(/"/g, '""')}"` : text;
};

/** Iterate row indexes: the given order, or 0..n-1. */
const rowsOf = (table: ArrowTable, rowIndexes?: readonly number[]): readonly number[] =>
  rowIndexes ?? Array.from({ length: table.numRows }, (_, i) => i);

export const tableToDelimited = (
  table: ArrowTable,
  opts: { delimiter: ',' | '\t'; rowIndexes?: readonly number[]; header?: boolean; nullText?: string | null },
): string => {
  const { delimiter, rowIndexes, header = true, nullText = null } = opts;
  const fields = table.schema.fields;
  const vectors = fields.map((f) => table.getChild(f.name)!);
  const lines: string[] = [];
  if (header) lines.push(fields.map((f) => quoteField(f.name, delimiter)).join(delimiter));
  for (const r of rowsOf(table, rowIndexes)) {
    lines.push(vectors.map((vec, c) => {
      const v = vec.get(r);
      return quoteField(v === null || v === undefined ? nullText : formatArrowValue(v, fields[c].type), delimiter);
    }).join(delimiter));
  }
  return lines.join('\n') + '\n';
};

export const tableToJsonRecords = (table: ArrowTable, rowIndexes?: readonly number[]): Record<string, unknown>[] => {
  const fields = table.schema.fields;
  const vectors = fields.map((f) => table.getChild(f.name)!);
  const out: Record<string, unknown>[] = [];
  for (const r of rowsOf(table, rowIndexes)) {
    const rec: Record<string, unknown> = {};
    vectors.forEach((vec, c) => { rec[fields[c].name] = arrowValueToJson(vec.get(r), fields[c].type); });
    out.push(rec);
  }
  return out;
};

/** Excel workbook (one sheet) from the shown rows; xlsx is loaded on first use. */
export const tableToXlsxBlob = async (
  table: ArrowTable, opts: { rowIndexes?: readonly number[]; sheetName?: string } = {},
): Promise<Blob> => {
  const XLSX = await import('xlsx');
  const fields = table.schema.fields;
  const vectors = fields.map((f) => table.getChild(f.name)!);
  const aoa: unknown[][] = [fields.map((f) => f.name)];
  for (const r of rowsOf(table, opts.rowIndexes)) {
    aoa.push(vectors.map((vec, c) => {
      const v = vec.get(r);
      if (v === null || v === undefined) return null;
      const json = arrowValueToJson(v, fields[c].type);
      // Keep numbers/booleans/strings native for Excel; nested → JSON text.
      return typeof json === 'object' && json !== null ? JSON.stringify(json) : json;
    }));
  }
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, (opts.sheetName ?? 'results').slice(0, 31));
  const bytes = XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
  return new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};

let parquetReady: Promise<typeof import('parquet-wasm/esm')> | null = null;

/** Load + initialise parquet-wasm once (≈1.5 MB wasm, fetched on first export). */
const loadParquet = () => {
  if (!parquetReady) {
    parquetReady = (async () => {
      const [mod, wasmUrl] = await Promise.all([
        import('parquet-wasm/esm'),
        import('parquet-wasm/esm/parquet_wasm_bg.wasm?url'),
      ]);
      await mod.default({ module_or_path: wasmUrl.default });
      return mod;
    })();
  }
  return parquetReady;
};

/** Parquet (ZSTD) written straight from the Arrow IPC stream the API returned. */
export const ipcToParquet = async (ipcBytes: Uint8Array): Promise<Uint8Array> => {
  const pq = await loadParquet();
  const props = new pq.WriterPropertiesBuilder().setCompression(pq.Compression.ZSTD).build();
  return pq.writeParquet(pq.Table.fromIPCStream(ipcBytes), props);
};

/**
 * Save a blob as a file through a temporary link. Its synthetic click must not bubble: in Safari, Monaco (once any
 * editor has loaded) answers every click on the page with a clipboard write, and a click that isn't a user gesture,
 * like this one after an async export (Parquet waits for parquet-wasm), makes that write fail with NotAllowedError
 * (2026-10-15). The download is the link's default action, so it still happens.
 */
export const downloadBlob = (filename: string, blob: Blob) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.addEventListener('click', (e) => e.stopPropagation());
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Put plain text on the clipboard from a click or key handler: the browser's copy command first (it works in
 * Safari and over plain HTTP, see gridSelection), the async Clipboard API only if that didn't take.
 */
export const copyText = async (text: string): Promise<void> => {
  if (copyWithCopyCommand(text)) return;
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  throw new Error('This browser blocked access to the clipboard.');
};

/** Safe file stem from a cluster name + timestamp. */
export const resultFileStem = (clusterName: string): string => {
  const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  return `${clusterName.replace(/[^A-Za-z0-9_-]+/g, '_')}-query-${ts}`;
};
